import { useEffect, useMemo, useState } from "react";
import { Copy, Check, Loader2, RefreshCw, Plus, Trash2, Code2, Pencil } from "lucide-react";
import api, { formatDetail, BACKEND_URL } from "@/lib/api";
import Modal from "@/components/Modal";
import { SecurityPanel } from "@/pages/partner/SecurityPanel";
import FieldLockEditor from "@/components/FieldLockEditor";
import AutoDisablePanel from "@/components/AutoDisablePanel";

export function UniversalLink() {
    const [info, setInfo] = useState(null);
    const [pay, setPay] = useState(null);
    const [savingTimer, setSavingTimer] = useState(false);
    const [copied, setCopied] = useState(false);
    const [copiedShort, setCopiedShort] = useState(false);
    const load = async () => {
        const [r1, r2] = await Promise.all([
            api.get("/partner/universal-link"),
            api.get("/partner/payment-settings"),
        ]);
        setInfo(r1.data); setPay(r2.data);
    };
    useEffect(() => { load(); }, []);
    const link = info ? `${window.location.origin}/pay/${info.link_code}` : "";
    const safeCopy = async (text, setter) => {
        try { await navigator.clipboard.writeText(text); }
        catch (e) { console.warn("clipboard write denied:", e); /* The user can still hand-copy from the input. */ }
        setter(true); setTimeout(() => setter(false), 1500);
    };
    const copy = () => safeCopy(link, setCopied);
    const toggle = async (key, val) => {
        const next = { ...info.settings, [key]: val };
        await api.patch("/partner/universal-link", next); load();
    };
    const saveTimer = async (mins) => {
        setSavingTimer(true);
        try { await api.patch("/partner/payment-settings", { payment_session_minutes: mins }); await load(); }
        catch (e) { alert(formatDetail(e?.response?.data?.detail) || "Save failed"); }
        finally { setSavingTimer(false); }
    };
    const saveStrategy = async (strategy) => {
        setSavingTimer(true);
        try { await api.patch("/partner/payment-settings", { intent_strategy: strategy }); await load(); }
        catch (e) { alert(formatDetail(e?.response?.data?.detail) || "Save failed"); }
        finally { setSavingTimer(false); }
    };
    if (!info || !pay) return <div className="text-slate-500">Loading…</div>;
    const shortLink = info ? `${window.location.origin}/p/${info.link_code}` : "";
    const copyShort = () => safeCopy(shortLink, setCopiedShort);
    return (
        <div className="space-y-5 max-w-3xl">
            <div>
                <h1 className="text-2xl font-bold tracking-tight">Universal payment link</h1>
                <p className="text-sm text-slate-500 mt-1">Share this link. Customers enter amount + username to pay.</p>
            </div>
            <div className="card p-5 space-y-4">
                <div>
                    <label className="label">Your link</label>
                    <div className="flex gap-2">
                        <input data-testid="universal-link" readOnly className="input font-mono text-sm" value={link} />
                        <button data-testid="copy-link" onClick={copy} className="btn-secondary px-3 py-2 rounded-md">{copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}</button>
                    </div>
                </div>
                <div>
                    <label className="label">Short URL <span className="text-[10px] font-medium text-slate-400 normal-case">(easier to share over SMS / WhatsApp)</span></label>
                    <div className="flex gap-2">
                        <input data-testid="short-link" readOnly className="input font-mono text-sm" value={shortLink} />
                        <button data-testid="copy-short-link" onClick={copyShort} className="btn-secondary px-3 py-2 rounded-md">{copiedShort ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}</button>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <ToggleRow label="Link enabled" value={info.settings.enabled} onChange={(v) => toggle("enabled", v)} testId="link-enabled" />
                    <ToggleRow label="Require username" value={info.settings.require_username} onChange={(v) => toggle("require_username", v)} testId="link-require-username" />
                </div>
            </div>

            <div className="card p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                        <h3 className="font-semibold">Payment session timer</h3>
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                            How long the QR / bank details stay live before the session expires. Once expired, customers can still submit a UTR if they paid (we&apos;ll verify manually). Default is 6 minutes.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <input
                            data-testid="payment-session-minutes"
                            type="number" min={2} max={60}
                            defaultValue={pay.payment_session_minutes}
                            onBlur={(e) => {
                                const v = parseInt(e.target.value, 10);
                                if (Number.isFinite(v) && v !== pay.payment_session_minutes) saveTimer(v);
                            }}
                            className="input w-24 text-center font-mono"
                        />
                        <span className="text-xs text-slate-500">min</span>
                        {savingTimer && <span className="text-[11px] text-slate-400">saving…</span>}
                    </div>
                </div>
            </div>

            <div className="card p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                        <h3 className="font-semibold">Default UPI app for first-time customers</h3>
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                            When a customer has no payment history, which app should we surface first on the payment page? Choose <strong>Most popular</strong> for the simplest signal, or <strong>Highest converting</strong> to auto-route to the app that actually closes more deposits — even if it&apos;s less-tapped.
                        </p>
                    </div>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                    <button
                        data-testid="intent-strategy-tap_count"
                        onClick={() => saveStrategy("tap_count")}
                        className={`text-left p-3 rounded-md border-2 transition-all ${pay.intent_strategy === "tap_count" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
                        <div className="font-medium text-sm flex items-center gap-2">
                            Most popular
                            {pay.intent_strategy === "tap_count" && <span className="text-[10px] font-semibold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">Active</span>}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1">Pick the most-tapped app per ticket size. Default — works well for any traffic level.</div>
                    </button>
                    <button
                        data-testid="intent-strategy-conversion_rate"
                        onClick={() => saveStrategy("conversion_rate")}
                        className={`text-left p-3 rounded-md border-2 transition-all ${pay.intent_strategy === "conversion_rate" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
                        <div className="font-medium text-sm flex items-center gap-2">
                            Highest converting
                            {pay.intent_strategy === "conversion_rate" && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Active</span>}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1">Pick the app with the best tap-to-success ratio (≥ {pay.intent_min_taps || 3} taps required). Lifts completion when one app converts much better than another.</div>
                    </button>
                </div>
            </div>
        </div>
    );
}

function ToggleRow({ label, value, onChange, testId }) {
    return (
        <div className="flex items-center justify-between p-3 rounded-md bg-slate-50">
            <span className="text-sm">{label}</span>
            <button data-testid={testId} onClick={() => onChange(!value)} className={`relative w-11 h-6 rounded-full transition-colors ${value ? "bg-emerald-500" : "bg-slate-300"}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${value ? "left-5" : "left-0.5"}`} />
            </button>
        </div>
    );
}

export function ApiCredentials() {
    const [data, setData] = useState(null);
    const [secret, setSecret] = useState(null);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState("");
    const load = async () => { const r = await api.get("/partner/api-credentials"); setData(r.data); };
    useEffect(() => { load(); }, []);
    const rotate = async () => {
        if (!window.confirm("Rotate keys? Old secret will stop working immediately.")) return;
        setBusy(true);
        try { const r = await api.post("/partner/api-credentials/rotate"); setSecret(r.data); load(); }
        finally { setBusy(false); }
    };
    const copy = (k, v) => { navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(""), 1500); };
    if (!data) return <div className="text-slate-500">Loading…</div>;
    return (
        <div className="space-y-5 max-w-4xl">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">API credentials</h1>
                    <p className="text-sm text-slate-500 mt-1">Authenticate server-to-server requests.</p>
                </div>
                <button data-testid="rotate-creds" onClick={rotate} disabled={busy} className="btn-secondary px-4 py-2 rounded-md text-sm flex items-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Rotate</button>
            </div>
            <div className="card p-5 space-y-3">
                <Row k="API key" v={data.api_key} onCopy={() => copy("k", data.api_key)} copied={copied === "k"} />
                <Row k="Link code" v={data.link_code} onCopy={() => copy("c", data.link_code)} copied={copied === "c"} />
                <div className="text-xs text-slate-500 mt-2">{data.note}</div>
            </div>

            <ApiIntegrationDocs data={data} />

            {secret && (
                <Modal testId="rotate-result" onClose={() => setSecret(null)} title="New API secret (copy now)">
                    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 mb-3"> This secret will not be shown again.</div>
                    <Row k="API secret" v={secret.api_secret} onCopy={() => copy("s", secret.api_secret)} copied={copied === "s"} />
                </Modal>
            )}
        </div>
    );
}

function ApiIntegrationDocs({ data }) {
    const [copied, setCopied] = useState("");
    const copy = (k, v) => { navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(""), 1500); };
    const linkCode = data.link_code;
    const baseUrl = BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
    const initiateUrl = `${baseUrl}/api/public/pay/${linkCode}/initiate`;
    const methodsUrl = `${baseUrl}/api/public/pay/${linkCode}/methods`;
    const statusUrl = `${baseUrl}/api/public/payment-page/{txn_id}/status`;

    const sampleBody = useMemo(() => JSON.stringify({
        amount: 1500,
        username: "rohan_k",
        external_customer_id: "your_user_id_42",
        method_preference: "any",
    }, null, 2), []);

    const sampleResponse = useMemo(() => JSON.stringify({
        ok: true,
        transaction_id: "txn_abc123",
        method: "upi",
        snapshot: { label: "UPI Alpha", upi_id: "merchant@oksbi", payee_name: "Acme" },
        amount: 1500,
        expires_at: "2026-04-26T00:30:00Z",
    }, null, 2), []);

    const curlSample = `curl -X POST '${initiateUrl}' \\
  -H 'Content-Type: application/json' \\
  -d '${sampleBody.replace(/\n\s*/g, " ")}'

# → returns transaction_id. Redirect customer to:
#   ${baseUrl}/payment/<transaction_id>`;

    const callbackSample = `# After approve / reject, customer is redirected to your URL with these query params:
# ?txn_id=<id>&status=success|failed&utr=<UTR>
# Configure success_redirect / failure_redirect in Partner → Universal Link.

# To poll instead, hit:
${statusUrl}
# Returns {status: "pending"|"submitted"|"success"|"failed", redirect_url, completed_at}`;

    return (
        <div className="card overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 bg-slate-50/60">
                <Code2 className="w-4 h-4 text-blue-600" />
                <span className="font-semibold text-sm">REST integration — Create Payment</span>
                <span className="pill pill-info ml-auto">v1</span>
            </div>
            <div className="p-5 space-y-4">
                <p className="text-sm text-slate-600">
                    Drop PayGateway into your backend in three calls: <span className="font-medium">create</span> a payment, <span className="font-medium">redirect</span> the customer to our hosted payment page, then <span className="font-medium">listen</span> for the redirect callback or poll for status. Pass your own customer id as <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">external_customer_id</code> so the smart-routing engine assigns 1st/2nd/3rd-deposit channels correctly.
                </p>

                <div>
                    <SectionHeading n="1" title="Endpoints" />
                    <div className="space-y-2 text-sm">
                        <EndpointRow method="POST" path={initiateUrl} note="Create a payment, returns transaction_id" copyKey="ep1" copied={copied} onCopy={copy} />
                        <EndpointRow method="POST" path={methodsUrl} note="Preview which methods are available for an amount" copyKey="ep2" copied={copied} onCopy={copy} />
                        <EndpointRow method="GET"  path={statusUrl}  note="Poll status + redirect_url after approval" copyKey="ep3" copied={copied} onCopy={copy} />
                    </div>
                </div>

                <div>
                    <SectionHeading n="2" title="Request body" />
                    <CodeBlock id="body" code={sampleBody} copy={copy} copied={copied === "body"} />
                    <ul className="text-xs text-slate-500 mt-2 space-y-1 leading-relaxed">
                        <li><code className="font-mono">amount</code> — INR, must be within your configured channel min/max.</li>
                        <li><code className="font-mono">username</code> — display name for your customer (shown on payment page).</li>
                        <li><code className="font-mono">external_customer_id</code> — <strong>recommended.</strong> Your stable user id. Drives FTD / 2nd / 3rd-deposit category routing.</li>
                        <li><code className="font-mono">method_preference</code> — <code>&quot;upi&quot;</code>, <code>&quot;bank&quot;</code> or <code>&quot;any&quot;</code> (default).</li>
                    </ul>
                </div>

                <div>
                    <SectionHeading n="3" title="Sample response" />
                    <CodeBlock id="resp" code={sampleResponse} copy={copy} copied={copied === "resp"} />
                    <p className="text-xs text-slate-500 mt-2">
                        Send your customer to <code className="font-mono">{baseUrl}/payment/&lt;transaction_id&gt;</code> — they&apos;ll see the QR / UPI / bank details and submit their UTR. We auto-approve via SMS where possible.
                    </p>
                </div>

                <div>
                    <SectionHeading n="4" title="curl quick-start" />
                    <CodeBlock id="curl" code={curlSample} copy={copy} copied={copied === "curl"} />
                </div>

                <div>
                    <SectionHeading n="5" title="Redirect callback / status polling" />
                    <CodeBlock id="cb" code={callbackSample} copy={copy} copied={copied === "cb"} />
                </div>
            </div>
        </div>
    );
}

function SectionHeading({ n, title }) {
    return (
        <div className="flex items-center gap-2 mb-2">
            <span className="w-5 h-5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-bold flex items-center justify-center">{n}</span>
            <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{title}</span>
        </div>
    );
}

function EndpointRow({ method, path, note, copyKey, copied, onCopy }) {
    const colour = method === "POST" ? "bg-emerald-100 text-emerald-700" : method === "GET" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700";
    return (
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${colour}`}>{method}</span>
            <code className="font-mono text-xs flex-1 truncate">{path}</code>
            <button data-testid={`copy-endpoint-${copyKey}`} onClick={() => onCopy(copyKey, path)} className="btn-secondary px-2 py-1 rounded-md shrink-0">
                {copied === copyKey ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
            <span className="text-[11px] text-slate-500 hidden sm:inline ml-1 truncate">{note}</span>
        </div>
    );
}

function CodeBlock({ id, code, copy, copied }) {
    return (
        <div className="relative bg-slate-900 text-slate-100 rounded-md overflow-hidden">
            <button data-testid={`copy-code-${id}`} onClick={() => copy(id, code)} className="absolute top-2 right-2 text-slate-300 hover:text-white bg-slate-700/70 hover:bg-slate-700 px-2 py-1 rounded-md text-[11px] flex items-center gap-1">
                {copied ? <Check className="w-3 h-3 text-emerald-300" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copied" : "Copy"}
            </button>
            <pre className="overflow-x-auto p-4 text-[12px] leading-relaxed font-mono whitespace-pre">{code}</pre>
        </div>
    );
}

function Row({ k, v, onCopy, copied }) {
    return (
        <div className="flex items-center gap-2">
            <div className="w-24 text-xs font-medium text-slate-500">{k}</div>
            <input readOnly value={v} className="input font-mono text-xs flex-1" />
            <button onClick={onCopy} className="btn-secondary px-2.5 py-2 rounded-md">{copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}</button>
        </div>
    );
}

// Canonical page-permission keys. MUST match the `perm` annotations on
// PartnerShell's NAV array so the sidebar filter and the role-editor stay
// in lockstep. Keep alphabetically grouped within domains for clarity.
const TEAM_PAGE_KEYS = [
    { key: "dashboard", label: "Dashboard" },
    { key: "upi", label: "UPI Channels" },
    { key: "banks", label: "Bank Accounts" },
    { key: "transactions", label: "Transactions" },
    { key: "success_txns", label: "Successful Txns" },
    { key: "approvals", label: "Approvals" },
    { key: "customers", label: "Customers" },
    { key: "sms", label: "SMS Verify" },
    { key: "statements", label: "Statements" },
    { key: "aa", label: "Bank Aggregator" },
    { key: "link", label: "Universal Link" },
    { key: "links", label: "Payment Links" },
    { key: "analytics", label: "Analytics" },
    { key: "alerts", label: "Alerts" },
    { key: "credentials", label: "API Credentials" },
    { key: "webhooks", label: "Webhooks" },
    { key: "invoices", label: "Invoices" },
];

export function Team() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const load = async () => { const r = await api.get("/partner/team"); setItems(r.data.items); };
    useEffect(() => { load(); }, []);
    const remove = async (uid) => { if (!window.confirm("Remove this user?")) return; await api.delete(`/partner/team/${uid}`); load(); };
    return (
        <div className="space-y-5">
            <div className="flex items-end justify-between">
                <div><h1 className="text-2xl font-bold tracking-tight">Team</h1><p className="text-sm text-slate-500 mt-1">Manage team members and per-page permissions.</p></div>
                <button data-testid="add-team" onClick={() => setOpen(true)} className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Add user</button>
            </div>
            <div className="card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-2.5 text-left">Name</th><th className="px-4 py-2.5 text-left">Email</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5 text-left">Pages</th><th></th></tr></thead>
                    <tbody>
                        {items.map((u) => {
                            const perms = u.permissions || {};
                            const granted = Object.entries(perms).filter(([, v]) => v && v !== "none");
                            return (
                                <tr key={u.user_id} className="border-b border-slate-100 last:border-0" data-testid={`team-row-${u.user_id}`}>
                                    <td className="px-4 py-3 font-medium">{u.name}</td>
                                    <td className="px-4 py-3 text-slate-600">{u.email}</td>
                                    <td className="px-4 py-3 text-center"><span className="pill pill-info">{u.role}</span></td>
                                    <td className="px-4 py-3 text-[11px] text-slate-500">
                                        {u.role === "partner_admin" ? <span className="text-emerald-700 font-medium">All pages</span>
                                         : granted.length === 0 ? <span className="text-slate-400">None granted</span>
                                         : `${granted.length} page${granted.length === 1 ? "" : "s"}`}
                                        {u.feature_flags?.export_success && <span className="ml-2 text-amber-700">+Export</span>}
                                    </td>
                                    <td className="px-4 py-3 text-right space-x-1">
                                        <button data-testid={`team-edit-${u.user_id}`} onClick={() => setEditUser(u)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded"><Pencil className="w-4 h-4" /></button>
                                        <button data-testid={`team-remove-${u.user_id}`} onClick={() => remove(u.user_id)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded"><Trash2 className="w-4 h-4" /></button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {open && <TeamFormModal onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
            {editUser && <TeamFormModal user={editUser} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); load(); }} />}
        </div>
    );
}

function TeamFormModal({ user, onClose, onDone }) {
    const isEdit = !!user;
    const [f, setF] = useState({
        name: user?.name || "",
        email: user?.email || "",
        password: "",
        role: user?.role || "partner_team",
        permissions: user?.permissions || {},
        feature_flags: user?.feature_flags || {},
    });
    const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
    const setPerm = (key, value) => setF((s) => ({ ...s, permissions: { ...s.permissions, [key]: value } }));
    const setFlag = (key, value) => setF((s) => ({ ...s, feature_flags: { ...s.feature_flags, [key]: value } }));
    const isAdmin = f.role === "partner_admin";

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            if (isEdit) {
                const payload = {
                    name: f.name,
                    role: f.role,
                    permissions: f.permissions,
                    feature_flags: f.feature_flags,
                };
                if (f.password) payload.password = f.password;
                await api.patch(`/partner/team/${user.user_id}`, payload);
            } else {
                await api.post("/partner/team", f);
            }
            onDone();
        } catch (e2) {
            setErr(formatDetail(e2?.response?.data?.detail));
        } finally { setBusy(false); }
    };
    return (
        <Modal testId={isEdit ? "edit-team-modal" : "add-team-modal"} onClose={onClose} title={isEdit ? `Edit ${user.name}` : "Add team user"}>
            <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <div><label className="label">Name</label><input data-testid="tu-name" required className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
                    <div><label className="label">Email</label><input data-testid="tu-email" required type="email" className="input" disabled={isEdit} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="label">{isEdit ? "Password (leave blank to keep)" : "Password"}</label>
                        <input data-testid="tu-pw" required={!isEdit} type="password" minLength={10} className="input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder={isEdit ? "•••••• (unchanged)" : ""} />
                        {f.password && (
                            <ul className="text-[11px] text-slate-500 grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1.5">
                                {[
                                    ["≥10 chars", (s) => s.length >= 10],
                                    ["a-z", (s) => /[a-z]/.test(s)],
                                    ["A-Z", (s) => /[A-Z]/.test(s)],
                                    ["0-9", (s) => /\d/.test(s)],
                                    ["symbol", (s) => /[!@#$%^&*()_+\-=\[\]{};:'",.<>/?\\|`~]/.test(s)],
                                ].map(([label, test]) => (
                                    <li key={label} className={test(f.password) ? "text-emerald-600" : ""}>
                                        {test(f.password) ? "✓" : "·"} {label}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div><label className="label">Role</label><select data-testid="tu-role" className="input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}><option value="partner_team">Team member</option><option value="partner_admin">Partner admin</option></select></div>
                </div>

                <div className={`border-t border-slate-200 pt-3 ${isAdmin ? "opacity-50 pointer-events-none" : ""}`}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center justify-between">
                        <span>Page permissions</span>
                        {isAdmin && <span className="text-emerald-700 normal-case font-normal">Admins have full access</span>}
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto pr-1">
                        {TEAM_PAGE_KEYS.map((p) => {
                            const v = f.permissions[p.key] || "none";
                            return (
                                <div key={p.key} className="flex items-center justify-between gap-2 text-xs">
                                    <span className="text-slate-700">{p.label}</span>
                                    <select data-testid={`tu-perm-${p.key}`} className="input !py-1 !text-[11px] w-24"
                                            value={v} onChange={(e) => setPerm(p.key, e.target.value)}>
                                        <option value="none">No access</option>
                                        <option value="read">Read</option>
                                        <option value="write">Read &amp; write</option>
                                    </select>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className={`border-t border-slate-200 pt-3 ${isAdmin ? "opacity-50 pointer-events-none" : ""}`}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Feature flags</div>
                    <label className="flex items-start gap-2 cursor-pointer text-xs">
                        <input data-testid="tu-flag-export" type="checkbox" className="mt-0.5"
                               checked={!!f.feature_flags.export_success}
                               onChange={(e) => setFlag("export_success", e.target.checked)} />
                        <div>
                            <div className="font-medium text-slate-800">Allow CSV export on Successful Transactions</div>
                            <div className="text-[10px] text-slate-500">When off, the agent sees the page (subject to page perms) but cannot download CSVs.</div>
                        </div>
                    </label>
                </div>

                {isEdit && !isAdmin && (
                    <div className="border-t border-slate-200 pt-3">
                        <FieldLockEditor scope="partner" userId={user.user_id} />
                    </div>
                )}

                {err && <div data-testid="tu-err" className="text-sm text-rose-600">{err}</div>}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 rounded-md text-sm">Cancel</button>
                    <button data-testid="tu-submit" type="submit" disabled={busy} className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? "Save changes" : "Add"}</button>
                </div>
            </form>
        </Modal>
    );
}

export function Profile() {
    const [data, setData] = useState(null);
    const [edit, setEdit] = useState(false);
    const [f, setF] = useState({ business_name: "", contact_email: "", contact_phone: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const reload = () => api.get("/partner/profile").then((r) => {
        setData(r.data);
        setF({
            business_name: r.data.partner?.business_name || "",
            contact_email: r.data.partner?.contact_email || "",
            contact_phone: r.data.partner?.contact_phone || "",
        });
    });
    useEffect(() => { reload(); }, []);
    const save = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try { await api.patch("/partner/profile", f); setEdit(false); await reload(); }
        catch (e2) { setErr(e2?.response?.data?.detail || e2.message); }
        finally { setBusy(false); }
    };
    if (!data) return <div className="text-slate-500">Loading…</div>;
    return (
        <div className="space-y-8 max-w-3xl">
            <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div><h1 className="text-2xl font-bold tracking-tight">Profile</h1><p className="text-sm text-slate-500 mt-1">Account & business details.</p></div>
                {!edit && (
                    <button data-testid="profile-edit-btn" onClick={() => setEdit(true)}
                            className="btn-secondary px-3 py-1.5 rounded-md text-xs">Edit business details</button>
                )}
            </div>
            {!edit ? (
                <div className="card p-5 grid grid-cols-2 gap-4 text-sm">
                    <Field k="Business" v={data.partner?.business_name} />
                    <Field k="Status" v={<span className="pill pill-success">{data.partner?.status}</span>} />
                    <Field k="Contact email" v={data.partner?.contact_email} />
                    <Field k="Phone" v={data.partner?.contact_phone} />
                    <Field k="Your name" v={data.user?.name} />
                    <Field k="Your email" v={data.user?.email} />
                    <Field k="Your role" v={data.user?.role} />
                    <Field k="Link code" v={data.partner?.link_code} />
                </div>
            ) : (
                <form onSubmit={save} className="card p-5 space-y-3 text-sm">
                    <div>
                        <label className="label">Business name</label>
                        <input data-testid="profile-business" required maxLength={120} className="input"
                               value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} />
                    </div>
                    <div>
                        <label className="label">Contact email</label>
                        <input data-testid="profile-email" required type="email" className="input"
                               value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} />
                    </div>
                    <div>
                        <label className="label">Contact phone</label>
                        <input data-testid="profile-phone" className="input"
                               value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} />
                    </div>
                    {err && <div className="text-rose-600 text-sm">{err}</div>}
                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                        <button type="button" onClick={() => setEdit(false)} className="btn-secondary px-4 py-2 rounded-md">Cancel</button>
                        <button data-testid="profile-save-btn" type="submit" disabled={busy}
                                className="btn-primary px-4 py-2 rounded-md flex items-center gap-2">
                            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
                        </button>
                    </div>
                </form>
            )}
            </div>
            <SecurityPanel />
            <AutoDisablePanel scope="partner" showClearOverride />
        </div>
    );
}

function Field({ k, v }) {
    return <div><div className="text-xs uppercase tracking-wider text-slate-500">{k}</div><div className="mt-1 font-medium">{v || "—"}</div></div>;
}
